/**
 * Portal multi-account "pay across my accounts" view (PD-14190). Shown on the
 * Make Payment page when the portal user manages more than one account. Open
 * invoices are grouped by account; each account section has its own payment-
 * method selector that DEFAULTS to the logged-in account's method (picked at
 * the bottom) but can be OVERRIDDEN to that account's own saved method or its
 * own Account Funds. One submit fans out to one payment per account and the
 * result is reported per account.
 */

import { faPlus, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	formatPaymentMethodLabel,
	formatPaymentMethodOptionLabel,
	PaymentMethodTypeEnum,
	resolvePaymentMethodFeeRate,
} from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalPayAccountAllocationDto,
	PortalPayAccountResultDto,
	PortalPayAccountsContextDto,
} from '@supply-mover-2-0/shared';
import {
	formatPaymentRestrictionMessage,
	isPartialAccountFundsSpend,
	isZeroTotalCreditPayment,
	NO_PAYMENT_RESTRICTIONS,
} from '@supply-mover-2-0/shared';
import {
	CardBrandIcon,
	extractPaymentDecline,
	PaymentDeclineModal,
	Select,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { invalidatePaymentAvailability } from '../hooks/useCanMakePayment';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalPaymentService from '../services/portalPaymentService';
import { useLoginModalStore } from '../stores/loginModalStore';
import { CurrencyInput } from './CurrencyInput';
import {
	DEFAULT_HISTORY_RANGE,
	historyRangeOptions,
	resolveHistoryRange,
} from './HistoryFilterBar';
import { PaymentSummary } from './PaymentSummary';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';
import { PortalSidebar } from './PortalSidebar';

const ACCOUNT_FUNDS = 'Account Funds';
const DEFAULT_CHOICE = 'default';

interface LineStateModel {
	checked: boolean;
	amount: string;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

function parseAmount(raw: string): number {
	const parsed = parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

// PD-16381 — option labels and their fee disclosure (PD-15011/PD-15154/
// PD-15173) come from the shared payment-method-options helpers in
// libs/core, the same ones the Pay Invoice sidebar and single-account Make
// Payment use, so the three surfaces cannot describe one method differently.

export const PayAccountsView: React.FC = () => {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const openLoginModal = useLoginModalStore((s) => s.open);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const [context, setContext] = useState<PortalPayAccountsContextDto | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [lines, setLines] = useState<Record<number, LineStateModel>>({});
	/** Per-account method choice keyed by clientId: 'default' | a saved-method
	 *  id (as a string). */
	const [accountChoice, setAccountChoice] = useState<Record<number, string>>(
		{},
	);
	/**
	 * PD-15585 — per-account "use this account's funds" opt-in, keyed by
	 * clientId. Never remembered and never on by default: the balance is only
	 * spent when the customer ticks the box beside the figure it would spend.
	 * Account Funds used to be a METHOD choice here, which let a $2 balance be
	 * selected against a $2,169.93 invoice — the page then quoted the full
	 * amount as the charge while the server refused the payment outright.
	 */
	const [useFundsByClient, setUseFundsByClient] = useState<
		Record<number, boolean>
	>({});
	/** Per-account on-account deposit amount (raw user input) keyed by clientId
	 *  (PD-14286). Money placed on that account's balance, not on an invoice. */
	const [onAccountByClient, setOnAccountByClient] = useState<
		Record<number, string>
	>({});
	const [defaultMethodId, setDefaultMethodId] = useState<number | null>(null);

	// Relative range key (matching the history surfaces) plus the explicit
	// bounds that only apply when the key is 'custom'. The effective
	// from/to are derived below, so the filtering logic stays unchanged.
	const [rangeKey, setRangeKey] = useState(DEFAULT_HISTORY_RANGE);
	const [customFrom, setCustomFrom] = useState('');
	const [customTo, setCustomTo] = useState('');
	const [overdueOnly, setOverdueOnly] = useState(false);
	// PD-15245 — hide accounts that are only listed because Account Funds
	// deposits are possible (no open invoices matching the filters). OFF by
	// default so the deposit-only rows stay reachable.
	const [withInvoicesOnly, setWithInvoicesOnly] = useState(false);
	// PD-14285 — narrow the (potentially long) multi-account list to a single
	// location; null = "All locations" (the default, view-all behaviour).
	const [locationFilter, setLocationFilter] = useState<number | null>(null);
	const selectAllRef = useRef<HTMLInputElement>(null);
	/**
	 * PD-15585 — how far down the viewport the site header ends, so each
	 * account's header can stick immediately below it. Measured rather than
	 * hardcoded: the portal header collapses its logo row on phones and adds
	 * the iOS safe-area inset on Capacitor, so its height is not one number.
	 */
	const [headerOffset, setHeaderOffset] = useState(0);
	useEffect(() => {
		const measure = (): void => {
			setHeaderOffset(document.querySelector('header')?.offsetHeight ?? 0);
		};
		measure();
		window.addEventListener('resize', measure);
		return (): void => {
			window.removeEventListener('resize', measure);
		};
	}, []);
	/** Default-method ids seen on the last fetch — a refetch that surfaces a
	 *  new id means the user just added that method (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	const [showAddPayment, setShowAddPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	// PD-15185 — a gateway decline, shown as a dialog rather than inline.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);
	const [results, setResults] =
		useState<Array<PortalPayAccountResultDto> | null>(null);

	const fetchContext = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		setLoading(true);
		setLoadError(null);
		try {
			const res = await portalPaymentService.getPayAccountsContext(token);
			if (res.success) {
				setContext(res.data);
				// PD-15014 — MERGE the per-invoice / per-account state instead of
				// re-initializing it. Adding a payment method mid-flow refetches
				// this context, and a full reset silently wiped every ticked
				// invoice and typed amount the user had staged. Rows for invoices
				// that no longer come back are pruned.
				setLines((prev) => {
					const next: Record<number, LineStateModel> = {};
					for (const acct of res.data.accounts) {
						for (const inv of acct.openInvoices) {
							next[inv.id] = prev[inv.id] ?? { checked: false, amount: '' };
						}
					}
					return next;
				});
				setAccountChoice((prev) => {
					const next: Record<number, string> = {};
					for (const acct of res.data.accounts) {
						next[acct.clientId] = prev[acct.clientId] ?? DEFAULT_CHOICE;
					}
					return next;
				});
				setUseFundsByClient((prev) => {
					const next: Record<number, boolean> = {};
					for (const acct of res.data.accounts) {
						if (prev[acct.clientId]) {
							next[acct.clientId] = true;
						}
					}
					return next;
				});
				setOnAccountByClient((prev) => {
					const next: Record<number, string> = {};
					for (const acct of res.data.accounts) {
						const kept = prev[acct.clientId] ?? '';
						if (kept !== '') {
							next[acct.clientId] = kept;
						}
					}
					return next;
				});
				const methods = res.data.defaultSavedPaymentMethods;
				// A method that appears on a refetch was just added through the
				// sidebar — select it, since paying with it is why it was added.
				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				const fresh =
					prevKnown == null ? null : methods.find((m) => !prevKnown.has(m.id));
				setDefaultMethodId((prev) => {
					if (fresh != null) {
						return fresh.id;
					}
					if (prev != null && methods.some((m) => m.id === prev)) {
						return prev;
					}
					const primary = methods.find((m) => m.isPrimary);
					return primary?.id ?? (methods.length > 0 ? methods[0].id : null);
				});
			}
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : t('Failed to load payment data'),
			);
		} finally {
			setLoading(false);
		}
	}, [token, t]);

	useEffect(() => {
		void fetchContext();
	}, [fetchContext]);

	const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

	// Effective bounds for the active range. Everything downstream still reads
	// plain ISO dates, so swapping the control changed no filtering logic.
	// 'all' resolves to no bounds, which is why these default to ''.
	const { dateFrom, dateTo } = useMemo(() => {
		const bounds = resolveHistoryRange(rangeKey, customFrom, customTo);
		return { dateFrom: bounds.dateFrom ?? '', dateTo: bounds.dateTo ?? '' };
	}, [rangeKey, customFrom, customTo]);

	// Accounts with their invoices narrowed by the Date/Overdue filters;
	// accounts left with no matching invoices are dropped from the view.
	const filteredAccounts = useMemo(
		() =>
			(context?.accounts ?? [])
				.filter(
					(acct) => locationFilter == null || acct.clientId === locationFilter,
				)
				.map((acct) => {
					const openInvoices = acct.openInvoices.filter((inv) => {
						// PD-15441 — credit rows are separated out below; the
						// payable list must never contain one (PD-15014).
						if (inv.isCreditMemo === true) {
							return false;
						}
						const d = inv.date?.slice(0, 10) ?? null;
						if (dateFrom !== '' && (d == null || d < dateFrom)) {
							return false;
						}
						if (dateTo !== '' && (d == null || d > dateTo)) {
							return false;
						}
						if (overdueOnly) {
							const due = inv.dueDate?.slice(0, 10) ?? null;
							if (due == null || due >= todayIso) {
								return false;
							}
						}
						return true;
					});
					// PD-15441 — the account's credit memos, deliberately EXEMPT
					// from the date/overdue filters (the single-account screen does
					// the same). Those filters narrow which invoices to pay; a
					// credit memo is not something being paid, and hiding it would
					// silently remove the customer's ability to offset the very
					// invoices the filter left on screen. "Overdue" cannot apply to
					// one at all — a credit memo has no due date. They do drop away
					// with the invoices though: with nothing left to offset, an
					// applied credit has no target.
					const creditMemos =
						openInvoices.length > 0
							? acct.openInvoices.filter((inv) => inv.isCreditMemo === true)
							: [];
					return { ...acct, openInvoices, creditMemos };
				})
				// Keep an account when it has invoices to pay, OR when a deposit
				// is possible on it (so an on-account deposit can be made even
				// with no open invoices) — PD-14286. The "Only show accounts
				// with invoices" checkbox (PD-15245) drops the deposit-only
				// rows so the page reads as a pay-invoices worklist.
				//
				// PD-14922 — a deposit needs the funds pool AND the tenant's
				// deposit switch. With deposits off, an invoice-less account has
				// nothing to do here: funds are only ever spent against an
				// invoice, and this account has none.
				.filter(
					(acct) =>
						acct.openInvoices.length > 0 ||
						(acct.accountFunds.enabled &&
							acct.accountFunds.depositAllowed &&
							!withInvoicesOnly),
				)
				// PD-15098 — the signed-in account leads the list. On a page of
				// visually-identical cards a deposit typed into "the" apply row
				// near the submit area landed on whichever account happened to
				// render last; the user's own account being first (and badged
				// below) makes the common case the safe case. Display order
				// only — every input stays keyed by clientId.
				.sort((a, b) => {
					const aIsPayer = a.clientId === context?.payerClientId ? 0 : 1;
					const bIsPayer = b.clientId === context?.payerClientId ? 0 : 1;
					return aIsPayer - bIsPayer;
				}),
		[
			context?.accounts,
			context?.payerClientId,
			dateFrom,
			dateTo,
			overdueOnly,
			withInvoicesOnly,
			todayIso,
			locationFilter,
		],
	);

	const filtersActive =
		rangeKey !== DEFAULT_HISTORY_RANGE ||
		overdueOnly ||
		withInvoicesOnly ||
		locationFilter != null;

	const outstandingById = useMemo(() => {
		const map = new Map<number, number>();
		for (const acct of context?.accounts ?? []) {
			for (const inv of acct.openInvoices) {
				map.set(inv.id, inv.outstandingBalance);
			}
		}
		return map;
	}, [context?.accounts]);

	/**
	 * PD-15441 — which rows are credit memos rather than payable invoices.
	 * Read from the RAW context (not the filtered view) so a cap or an error
	 * message is still correct for a row the current filters have hidden.
	 */
	const creditRowIds = useMemo(() => {
		const ids = new Set<number>();
		for (const acct of context?.accounts ?? []) {
			for (const inv of acct.openInvoices) {
				if (inv.isCreditMemo === true) {
					ids.add(inv.id);
				}
			}
		}
		return ids;
	}, [context?.accounts]);

	const checkedIds = useMemo(() => {
		const ids = new Set<number>();
		for (const [id, line] of Object.entries(lines)) {
			if (line.checked) {
				ids.add(Number(id));
			}
		}
		return ids;
	}, [lines]);

	const visibleIds = useMemo(
		() => filteredAccounts.flatMap((a) => a.openInvoices.map((i) => i.id)),
		[filteredAccounts],
	);
	const allChecked =
		visibleIds.length > 0 && visibleIds.every((id) => checkedIds.has(id));
	const someChecked = visibleIds.some((id) => checkedIds.has(id));

	useEffect(() => {
		if (selectAllRef.current != null) {
			selectAllRef.current.indeterminate = someChecked && !allChecked;
		}
	}, [someChecked, allChecked]);

	// Account funds is a tenant-level capability — every account block carries
	// the same `enabled` flag, so any one of them tells us whether on-account
	// deposits are possible at all (PD-14286).
	const accountFundsEnabled = (context?.accounts ?? []).some(
		(a) => a.accountFunds.enabled,
	);

	// PD-14922 — adding money to an account can be switched off per tenant while
	// SPENDING existing funds keeps working (an ERP may be unable to record an
	// on-account deposit). Tenant-level like `enabled` above, so `every` also
	// makes an empty account list read as allowed — matching the long-standing
	// behavior for a page with nothing on it yet.
	const accountFundsDepositAllowed = (context?.accounts ?? []).every(
		(a) => a.accountFunds.depositAllowed,
	);
	/** Whether an on-account deposit may be entered anywhere on this page. */
	const canDepositToAccount = accountFundsEnabled && accountFundsDepositAllowed;

	// PD-15585 — the tenant's ERP-driven blocks, with the portal wording.
	// Tenant-level; evaluated per account below against that account's own
	// balance and credit. Nothing blocked until the context says so.
	const restrictions = context?.paymentRestrictions ?? NO_PAYMENT_RESTRICTIONS;

	/**
	 * PD-15441 — every per-account money figure derived once, keyed by
	 * clientId: what its checked invoices come to, how much of its own credit
	 * is applied, its deposit, and the resulting NET.
	 *
	 * The submit fans out to one payment per account, so netting is
	 * necessarily per account too — a credit memo belongs to one client and
	 * can only offset that client's invoices. Deriving it in one place keeps
	 * the account card, the grand total, the fee preview, the guards and the
	 * request from each computing it slightly differently.
	 */
	const accountTotals = useMemo(() => {
		const map = new Map<
			number,
			{
				invoiceSum: number;
				creditApplied: number;
				deposit: number;
				net: number;
				/** PD-15585 — what this account's balance takes off its net. */
				fundsApplied: number;
				/** True when the balance settles the net outright (no method needed). */
				fundsCoverAll: boolean;
				availableCredit: number;
				creditExceedsInvoices: boolean;
				creditWithDeposit: boolean;
				fundsWithDeposit: boolean;
				partialFundsBlocked: boolean;
				zeroTotalBlocked: boolean;
			}
		>();
		for (const acct of filteredAccounts) {
			const invoiceSum = round2(
				acct.openInvoices.reduce(
					(sum, inv) =>
						sum +
						(lines[inv.id].checked ? parseAmount(lines[inv.id].amount) : 0),
					0,
				),
			);
			const creditApplied = round2(
				acct.creditMemos.reduce(
					(sum, memo) =>
						sum +
						(lines[memo.id].checked ? parseAmount(lines[memo.id].amount) : 0),
					0,
				),
			);
			const deposit = parseAmount(onAccountByClient[acct.clientId] ?? '');
			const net = round2(invoiceSum - creditApplied + deposit);
			// PD-15585 — the balance covers the INVOICE net first; a deposit is
			// money going ONTO the account and can never be paid for out of it,
			// so it is excluded from what the funds may absorb (the server
			// refuses the round trip).
			const useFunds = useFundsByClient[acct.clientId];
			const fundsTarget = round2(Math.max(0, invoiceSum - creditApplied));
			const available = acct.accountFunds.available;
			const fundsApplied =
				useFunds && fundsTarget > 0.005
					? round2(Math.min(available, fundsTarget))
					: 0;
			map.set(acct.clientId, {
				invoiceSum,
				creditApplied,
				deposit,
				net,
				fundsApplied,
				fundsCoverAll:
					useFunds && fundsTarget > 0.005 && available >= fundsTarget - 0.005,
				// PD-15585 — this account spending its funds for less than the
				// whole balance, and a $0 net that its applied credit fully
				// covers. Each is refused server-side the same way.
				partialFundsBlocked:
					restrictions.partialAccountFundsSpend.blocked &&
					useFunds &&
					isPartialAccountFundsSpend(fundsTarget, available),
				zeroTotalBlocked:
					restrictions.zeroTotalPayment.blocked &&
					isZeroTotalCreditPayment(net, creditApplied),
				availableCredit: round2(
					acct.creditMemos.reduce(
						(sum, memo) => sum + memo.outstandingBalance,
						0,
					),
				),
				// Applied credit records applications; it is never refunded, so it
				// may not exceed the invoices it offsets. Half-cent tolerance so
				// float noise in the inputs cannot raise a phantom error.
				creditExceedsInvoices: round2(invoiceSum - creditApplied) < -0.004,
				// The server refuses the combination outright: a deposit alongside
				// applied credit blurs what the credit paid for.
				creditWithDeposit: creditApplied > 0 && deposit > 0,
				// PD-15585 — and funds cannot fund a deposit back onto the same
				// account, so the two are refused together rather than silently
				// leaving the card to pay the deposit.
				fundsWithDeposit: useFunds && deposit > 0,
			});
		}
		return map;
	}, [
		filteredAccounts,
		lines,
		useFundsByClient,
		onAccountByClient,
		restrictions,
	]);

	// Grand total = each visible account's NET (invoices less applied credit,
	// plus any deposit), so the figure matches exactly what the submit will
	// charge across the fan-out.
	const total = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) => sum + (accountTotals.get(acct.clientId)?.net ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	/** PD-15585 — total taken off the charge by account balances. */
	const fundsAppliedTotal = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) =>
						sum + (accountTotals.get(acct.clientId)?.fundsApplied ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	/** Total credit applied across every visible account. */
	const creditAppliedTotal = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) =>
						sum + (accountTotals.get(acct.clientId)?.creditApplied ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	// PD-15688 — the GROSS invoice figure and the deposit figure, so the
	// summary can show what the invoices come to and deduct the credit from
	// it rather than presenting an already-netted number with the credit
	// described in a sentence beneath it.
	const invoiceSumTotal = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) =>
						sum + (accountTotals.get(acct.clientId)?.invoiceSum ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	const depositTotal = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) => sum + (accountTotals.get(acct.clientId)?.deposit ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	/**
	 * Accounts whose selection is invalid — blocks the whole submit. Covers
	 * the credit rules and the PD-15585 ERP-driven blocks alike.
	 */
	const hasAccountCreditErrors = useMemo(
		() =>
			filteredAccounts.some((acct) => {
				const totals = accountTotals.get(acct.clientId);
				return (
					totals?.creditExceedsInvoices === true ||
					totals?.creditWithDeposit === true ||
					totals?.fundsWithDeposit === true ||
					totals?.partialFundsBlocked === true ||
					totals?.zeroTotalBlocked === true
				);
			}),
		[filteredAccounts, accountTotals],
	);

	const lineErrors = useMemo(() => {
		const errors = new Map<number, string>();
		for (const [idStr, line] of Object.entries(lines)) {
			if (!line.checked) {
				continue;
			}
			const outstanding = outstandingById.get(Number(idStr));
			if (
				outstanding != null &&
				parseAmount(line.amount) > outstanding + 0.005
			) {
				// PD-15441 — a credit row's cap is the credit REMAINING on the
				// memo, so it needs its own wording; "outstanding balance" reads
				// as something the customer owes.
				errors.set(
					Number(idStr),
					creditRowIds.has(Number(idStr))
						? t('Exceeds remaining credit')
						: t('Exceeds outstanding balance'),
				);
			}
		}
		return errors;
	}, [lines, outstandingById, creditRowIds, t]);

	// PD-15014 — the processing fees this submit will actually add. The page
	// disclosed the fee RATE in the option labels (PD-15011) but the charged
	// total still silently exceeded the "Pay" figure. One payment is captured
	// per contributing account, so each account's fee is sized on its own
	// amount, with that account's effective (state-gated) rate and the funding
	// card's surchargeability — and the percentage is FLOORED per transaction,
	// matching the server's charge (PD-15174).
	const processingFees = useMemo(() => {
		let fees = 0;
		for (const acct of filteredAccounts) {
			const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
			const totals = accountTotals.get(acct.clientId);
			if (totals == null || totals.net <= 0) {
				continue;
			}
			// PD-15585 — Account Funds carries no fee (PD-15082), so the rate
			// falls only on the share the method actually collects. A net the
			// balance settles outright is charged nothing and carries no fee.
			if (totals.net - totals.fundsApplied <= 0.005) {
				continue;
			}
			const method =
				choice === DEFAULT_CHOICE
					? (context?.defaultSavedPaymentMethods ?? []).find(
							(m) => m.id === defaultMethodId,
						)
					: acct.savedPaymentMethods.find((m) => String(m.id) === choice);
			// PD-16381 — the same rule that prices the option labels: the
			// type's rate, only on a method that may be surcharged.
			const type =
				method != null
					? resolvePaymentMethodFeeRate(acct.paymentTypes, method)
					: null;
			if (type == null) {
				continue;
			}
			if (type.processingFeeType !== '%') {
				fees += type.processingFee;
				continue;
			}
			if (totals.creditApplied > 0) {
				// PD-15441 — mirror the server's sizing exactly when credit is
				// applied: each invoice line carries its OWN floored fee, and the
				// applied credit relieves the fee this payment adds (floored, and
				// capped at those new fees so a fee already carried inside an
				// invoice's balance is never clawed back). Flooring a single
				// pooled figure over the net can land a cent under what the card
				// is actually charged, and the disclosed total has to match the
				// charge to the cent.
				const newFees = acct.openInvoices.reduce((sum, inv) => {
					// PD-15688 — an invoice whose balance already embeds a fee is
					// charged as-is by the server (PD-15473) and adds no new fee.
					if (!lines[inv.id].checked || inv.unpaidProcessingFee > 0.005) {
						return sum;
					}
					return (
						sum +
						Math.floor(parseAmount(lines[inv.id].amount) * type.processingFee) /
							100
					);
				}, 0);
				const relief = Math.min(
					Math.floor(totals.creditApplied * type.processingFee) / 100,
					newFees,
				);
				// PD-15585 — account funds relieve the added fee the same way an
				// applied credit does: both shrink what the card collects.
				const fundsRelief = Math.min(
					Math.floor(totals.fundsApplied * type.processingFee) / 100,
					round2(newFees - relief),
				);
				fees += round2(newFees - relief - fundsRelief);
				continue;
			}
			// PD-15688 — surcharge only the portion not already carrying a fee.
			const feeBearingApplied = acct.openInvoices.reduce((sum, inv) => {
				if (!lines[inv.id].checked || inv.unpaidProcessingFee <= 0.005) {
					return sum;
				}
				return sum + parseAmount(lines[inv.id].amount);
			}, 0);
			const surchargeable = Math.max(
				0,
				totals.net - feeBearingApplied - totals.fundsApplied,
			);
			fees += Math.floor(surchargeable * type.processingFee) / 100;
		}
		return round2(fees);
	}, [
		filteredAccounts,
		accountTotals,
		lines,
		accountChoice,
		defaultMethodId,
		context?.defaultSavedPaymentMethods,
	]);

	// Does any account that is contributing (a checked invoice OR an on-account
	// deposit) rely on the default method? If so a default method is required.
	const anyUsesDefault = useMemo(
		() =>
			filteredAccounts.some((acct) => {
				if (
					(accountChoice[acct.clientId] ?? DEFAULT_CHOICE) !== DEFAULT_CHOICE
				) {
					return false;
				}
				// PD-15585 — an account its own balance settles outright is paid
				// without any method, so it does not make the default required.
				if (accountTotals.get(acct.clientId)?.fundsCoverAll === true) {
					return false;
				}
				const hasInvoice = acct.openInvoices.some(
					(inv) => lines[inv.id].checked,
				);
				const hasDeposit =
					parseAmount(onAccountByClient[acct.clientId] ?? '') > 0;
				return hasInvoice || hasDeposit;
			}),
		[filteredAccounts, accountChoice, accountTotals, lines, onAccountByClient],
	);

	// Is anything staged at all — a ticked invoice or a deposit on any account?
	const anyAccountContributing = useMemo(
		() =>
			filteredAccounts.some(
				(acct) =>
					acct.openInvoices.some((inv) => lines[inv.id].checked) ||
					parseAmount(onAccountByClient[acct.clientId] ?? '') > 0,
			),
		[filteredAccounts, lines, onAccountByClient],
	);

	// PD-15975 — the default method is only ever charged for an account left on
	// "Default method". Once every contributing account names its own method (or
	// Account Funds), nothing would use the default, so the control is disabled
	// rather than left looking like it still governs the charge. Switching any
	// account back to Default re-enables it immediately.
	const defaultMethodUnused = anyAccountContributing && !anyUsesDefault;

	/**
	 * The screen as it first renders, before the customer has chosen
	 * anything on any account. Nothing is wrong here — there is simply
	 * nothing to pay yet — so the reason panel stays away and the Pay button
	 * is quietly inert. Reasons appear once a choice has been made and
	 * something about it needs correcting.
	 */
	const nothingChosenYet = useMemo(
		() =>
			!Object.values(lines).some((line) => line.checked) &&
			!Object.values(useFundsByClient).some((use) => use) &&
			!Object.values(onAccountByClient).some(
				(amount) => parseAmount(amount) !== 0,
			),
		[lines, useFundsByClient, onAccountByClient],
	);

	/**
	 * PD-15585 — every reason Pay is greyed out, named with the account it
	 * belongs to. Derived from the same per-account totals `canSubmit` reads,
	 * so the list and the button cannot disagree.
	 */
	const blockingReasons = useMemo(() => {
		const reasons: Array<{
			key: string;
			account: string | null;
			message: string;
		}> = [];
		for (const acct of filteredAccounts) {
			const totals = accountTotals.get(acct.clientId);
			if (totals == null) {
				continue;
			}
			const name = acct.clientName ?? `#${acct.clientId}`;
			const push = (id: string, message: string): void => {
				reasons.push({ key: `${acct.clientId}:${id}`, account: name, message });
			};
			if (totals.creditExceedsInvoices) {
				push(
					'creditExceeds',
					'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
				);
			}
			if (totals.creditWithDeposit) {
				push(
					'creditWithDeposit',
					'Credit memos cannot be combined with an Apply to amount on the same account.',
				);
			}
			if (totals.fundsWithDeposit) {
				push(
					'fundsWithDeposit',
					'Account funds cannot pay for money added to the same account — clear the Apply to amount.',
				);
			}
			if (totals.partialFundsBlocked) {
				push(
					'partialFunds',
					formatPaymentRestrictionMessage(
						restrictions.partialAccountFundsSpend.message,
						formatCurrency(acct.accountFunds.available) ?? '',
					),
				);
			}
			if (totals.zeroTotalBlocked) {
				push('zeroTotal', restrictions.zeroTotalPayment.message);
			}
		}
		if (lineErrors.size > 0) {
			reasons.push({
				key: 'lineErrors',
				account: null,
				message: 'One or more amounts above exceed what is owed.',
			});
		}
		if (total < 0) {
			reasons.push({
				key: 'negativeTotal',
				account: null,
				message:
					'Applied credit exceeds the selected invoice amounts. Reduce the credit applied or select more invoices.',
			});
		} else if (total === 0 && creditAppliedTotal === 0) {
			reasons.push({
				key: 'nothingSelected',
				account: null,
				message: 'Select at least one invoice to pay.',
			});
		}
		if (anyUsesDefault && defaultMethodId == null) {
			reasons.push({
				key: 'noDefaultMethod',
				account: null,
				message:
					'Choose a default payment method for the accounts set to "Default method".',
			});
		}
		return reasons;
	}, [
		filteredAccounts,
		accountTotals,
		restrictions,
		lineErrors,
		total,
		creditAppliedTotal,
		anyUsesDefault,
		defaultMethodId,
	]);

	const canSubmit =
		!submitting &&
		// PD-15441 — a $0 total is submittable when applied credit fully covers
		// the selection: that payment exists to RECORD the applications, and
		// the server skips the gateway for it. A negative net, or credit with
		// no invoices to offset, stays blocked.
		total >= 0 &&
		(total > 0 || creditAppliedTotal > 0) &&
		!hasAccountCreditErrors &&
		lineErrors.size === 0 &&
		(!anyUsesDefault || defaultMethodId != null);

	// ─── Handlers ────────────────────────────────────────────────────
	const clearResults = (): void => {
		setResults(null);
		setSubmitError(null);
	};

	const toggleLine = (invoiceId: number, outstanding: number): void => {
		setLines((prev) => {
			const current = prev[invoiceId] ?? { checked: false, amount: '' };
			const checked = !current.checked;
			return {
				...prev,
				[invoiceId]: { checked, amount: checked ? outstanding.toFixed(2) : '' },
			};
		});
		clearResults();
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		setLines((prev) => ({ ...prev, [invoiceId]: { checked: true, amount } }));
		clearResults();
	};

	/**
	 * PD-15441 — toggling one of THIS account's credit memos. The fill is the
	 * smaller of the memo's remaining credit and what the account's currently
	 * selected invoices can still absorb, so checking a credit can never on
	 * its own push that account's net below zero. Everything is scoped to the
	 * one account: a memo belongs to a single client and the fan-out charges
	 * each client separately, so credit never crosses accounts.
	 */
	const toggleCreditMemo = (
		acct: {
			openInvoices: Array<{ id: number }>;
			creditMemos: Array<{ id: number }>;
		},
		memoId: number,
		remainingCredit: number,
	): void => {
		setLines((prev) => {
			const current = prev[memoId] ?? { checked: false, amount: '' };
			if (current.checked) {
				return { ...prev, [memoId]: { checked: false, amount: '' } };
			}
			const selectedInvoiceSum = acct.openInvoices.reduce((sum, inv) => {
				const line = prev[inv.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const otherCreditsApplied = acct.creditMemos.reduce((sum, other) => {
				if (other.id === memoId) {
					return sum;
				}
				const line = prev[other.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const absorbable = Math.max(
				0,
				round2(selectedInvoiceSum - otherCreditsApplied),
			);
			const fill = Math.min(remainingCredit, absorbable);
			return {
				...prev,
				[memoId]: { checked: true, amount: fill > 0 ? fill.toFixed(2) : '' },
			};
		});
		clearResults();
	};

	const toggleSelectAll = (): void => {
		const shouldCheck = !allChecked;
		setLines((prev) => {
			const next = { ...prev };
			for (const id of visibleIds) {
				next[id] = shouldCheck
					? { checked: true, amount: (outstandingById.get(id) ?? 0).toFixed(2) }
					: { checked: false, amount: '' };
			}
			return next;
		});
		clearResults();
	};

	// Per-account (per ownership/parent collection) select-all: toggles only
	// that account's currently-visible invoices, independent of the global one.
	const toggleAccountSelectAll = (invoices: Array<{ id: number }>): void => {
		const ids = invoices.map((inv) => inv.id);
		const allOn = ids.length > 0 && ids.every((id) => checkedIds.has(id));
		setLines((prev) => {
			const next = { ...prev };
			for (const id of ids) {
				next[id] = allOn
					? { checked: false, amount: '' }
					: {
							checked: true,
							amount: (outstandingById.get(id) ?? 0).toFixed(2),
						};
			}
			return next;
		});
		clearResults();
	};

	const accountName = (clientId: number): string =>
		(context?.accounts ?? []).find((a) => a.clientId === clientId)
			?.clientName ?? `#${clientId}`;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit || context == null) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		setResults(null);
		try {
			const accounts = filteredAccounts
				.map((acct): PortalPayAccountAllocationDto | null => {
					const invoiceAllocations = [
						...acct.openInvoices
							.filter(
								(inv) =>
									lines[inv.id].checked &&
									parseAmount(lines[inv.id].amount) > 0,
							)
							.map((inv) => ({
								invoiceId: inv.id,
								amount: parseAmount(lines[inv.id].amount),
							})),
						// PD-15441 — this account's credit applications go up
						// NEGATIVE, alongside its invoice lines: the server records
						// one application per document and charges only the net.
						...acct.creditMemos
							.filter(
								(memo) =>
									lines[memo.id].checked &&
									parseAmount(lines[memo.id].amount) > 0,
							)
							.map((memo) => ({
								invoiceId: memo.id,
								amount: -parseAmount(lines[memo.id].amount),
							})),
					];
					const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
					const onAccountAmount = parseAmount(
						onAccountByClient[acct.clientId] ?? '',
					);
					// Skip accounts that aren't contributing anything (no checked
					// invoices and no deposit) — PD-14286.
					if (invoiceAllocations.length === 0 && onAccountAmount <= 0) {
						return null;
					}
					const group: PortalPayAccountAllocationDto = {
						clientId: acct.clientId,
						invoiceAllocations,
					};
					if (onAccountAmount > 0) {
						group.onAccountAmount = onAccountAmount;
					}
					// PD-15585 — an account its balance settles outright goes up as
					// a plain Account Funds payment (no method to charge); one it
					// only partly covers rides the applyAccountFunds flag alongside
					// its chosen method, which collects the rest. Same shape the
					// cart submits (PD-15084).
					const totals = accountTotals.get(acct.clientId);
					if (totals?.fundsCoverAll === true) {
						group.overridePaymentMethodType = ACCOUNT_FUNDS;
					} else {
						if (choice !== DEFAULT_CHOICE) {
							group.overridePaymentMethodId = parseInt(choice, 10);
						}
						if (useFundsByClient[acct.clientId]) {
							group.applyAccountFunds = true;
						}
					}
					return group;
				})
				.filter((g): g is PortalPayAccountAllocationDto => g != null);

			const res = await portalPaymentService.payAccounts(token, {
				defaultPaymentMethodId: defaultMethodId ?? undefined,
				accounts,
			});
			// PD-16016 — each refusal is re-voiced for the customer, in the
			// dialog and in the results table alike; the API's decline sentence
			// is written for staff and its gateway detail may name the processor.
			const customerResults = res.results.map((result) => {
				const customerDecline = toCustomerDecline(
					result.paymentDecline ?? null,
				);

				return customerDecline == null
					? result
					: {
							...result,
							paymentDecline: customerDecline,
							error: customerDecline.message,
						};
			});

			setResults(customerResults);
			// PD-16789 — any account paid here may have had the last open
			// invoice; partial success counts too.
			void invalidatePaymentAvailability(queryClient);

			// PD-15185 — the fan-out pays each account separately, so a refusal
			// arrives inside the per-account results rather than as a thrown
			// error. Surface the first one; the results table below carries the
			// rest.
			const declined = customerResults.find(
				(result) => result.paymentDecline != null,
			);

			if (declined?.paymentDecline != null) {
				setDecline(declined.paymentDecline);
			}

			if (res.success) {
				window.setTimeout(() => {
					void navigate('/invoices');
				}, 2500);
			}
		} catch (err) {
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				setSubmitError(
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.'),
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Render ──────────────────────────────────────────────────────
	if (!isAuthenticated) {
		return <div />;
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-16">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (loadError != null) {
		return (
			<div className="p-4">
				<div className="p-4 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(loadError)}</p>
				</div>
			</div>
		);
	}

	const hasAnyAccounts = (context?.accounts.length ?? 0) > 0;

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<h1 className="text-lg md:text-xl font-bold text-default mb-1">
				{t('Make a Payment')}
			</h1>
			<p className="text-sm text-brand-muted mb-4">
				{canDepositToAccount
					? t('Pay open invoices or add funds to your accounts in one place.')
					: t('Pay open invoices across your accounts in one place.')}
			</p>

			{!hasAnyAccounts ? (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<p className="text-sm text-brand-muted">
						{t('No open invoices on any of your accounts.')}
					</p>
				</div>
			) : (
				<>
					{/* Filters + bulk select */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						{/*
						  Single-row filter header: an unlabeled global select-all
						  (across every account card), the Date range, Overdue only,
						  and Clear filters, all bottom-aligned (feedback 2026-06-17).
						*/}
						<div className="flex flex-wrap items-end gap-3">
							<Tooltip content={t('Select all')} position="top">
								<label className="flex items-center min-h-touch cursor-pointer">
									<input
										ref={selectAllRef}
										type="checkbox"
										aria-label={t('Select all')}
										checked={allChecked}
										disabled={visibleIds.length === 0}
										onChange={toggleSelectAll}
										className="w-4 h-4 text-portal-accent disabled:opacity-50"
									/>
								</label>
							</Tooltip>
							{/*
							  Location filter (PD-14285) — only worth showing when the
							  user actually has more than one account with open invoices;
							  options are the full account list (not the date/overdue-
							  narrowed view) so a location is always selectable.
							*/}
							{(context?.accounts.length ?? 0) > 1 && (
								<div className="min-w-[12rem]">
									<label className="block text-xs font-medium text-brand-muted mb-1">
										{t('Location')}
									</label>
									<select
										value={locationFilter == null ? '' : String(locationFilter)}
										onChange={(e) => {
											const value = e.target.value;
											setLocationFilter(
												value === '' ? null : parseInt(value, 10),
											);
										}}
										aria-label={t('Filter by location')}
										className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
									>
										<option value="">{t('All locations')}</option>
										{(context?.accounts ?? []).map((acct) => (
											<option key={acct.clientId} value={String(acct.clientId)}>
												{t(acct.clientName ?? `#${acct.clientId}`)}
											</option>
										))}
									</select>
								</div>
							)}
							{/*
							  PD-15016 — relative-range picker, matching the history
							  surfaces (Invoices / Orders / Statements / Transactions).
							  This page used to mount a two-month calendar popover, which
							  is both harder to operate for the common case ("the last
							  few months") and rendered a light calendar over the dark
							  brand page. Reusing `historyRangeOptions` keeps one date
							  vocabulary across the storefront; "Custom range" reveals
							  the explicit From/To inputs below for the rarer case.
							*/}
							<div className="min-w-[12rem]">
								<label className="block text-xs font-medium text-brand-muted mb-1">
									{t('Date')}
								</label>
								<select
									value={rangeKey}
									onChange={(e) => {
										setRangeKey(e.target.value);
										clearResults();
									}}
									aria-label={t('Filter by date range')}
									className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
								>
									{historyRangeOptions().map((opt) => (
										<option key={opt.key} value={opt.key}>
											{t(opt.label)}
										</option>
									))}
								</select>
							</div>
							{rangeKey === 'custom' && (
								<>
									<div className="min-w-[9rem]">
										<label className="block text-xs font-medium text-brand-muted mb-1">
											{t('From')}
										</label>
										<input
											type="date"
											value={customFrom}
											onChange={(e) => {
												setCustomFrom(e.target.value);
												clearResults();
											}}
											aria-label={t('From date')}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
										/>
									</div>
									<div className="min-w-[9rem]">
										<label className="block text-xs font-medium text-brand-muted mb-1">
											{t('To')}
										</label>
										<input
											type="date"
											value={customTo}
											onChange={(e) => {
												setCustomTo(e.target.value);
												clearResults();
											}}
											aria-label={t('To date')}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
										/>
									</div>
								</>
							)}
							<label className="flex items-center gap-2 cursor-pointer min-h-touch">
								<input
									type="checkbox"
									checked={overdueOnly}
									onChange={(e) => {
										setOverdueOnly(e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">
									{t('Overdue only')}
								</span>
							</label>
							{/* PD-15245 — drop the accounts that only appear because an
							    on-account deposit is possible (no matching invoices). */}
							<label className="flex items-center gap-2 cursor-pointer min-h-touch">
								<input
									type="checkbox"
									checked={withInvoicesOnly}
									onChange={(e) => {
										setWithInvoicesOnly(e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">
									{t('Only show accounts with invoices')}
								</span>
							</label>
							{filtersActive && (
								<button
									type="button"
									onClick={() => {
										setRangeKey(DEFAULT_HISTORY_RANGE);
										setCustomFrom('');
										setCustomTo('');
										setOverdueOnly(false);
										setWithInvoicesOnly(false);
										setLocationFilter(null);
									}}
									className="text-xs font-medium text-portal-accent hover:underline min-h-touch"
								>
									{t('Clear filters')}
								</button>
							)}
						</div>
					</div>

					{filteredAccounts.length === 0 ? (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
							<p className="text-sm text-brand-muted">
								{t('No invoices match your filters.')}
							</p>
						</div>
					) : (
						filteredAccounts.map((acct) => {
							const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
							const acctIds = acct.openInvoices.map((inv) => inv.id);
							const acctAllChecked =
								acctIds.length > 0 && acctIds.every((id) => checkedIds.has(id));
							const acctSomeChecked = acctIds.some((id) => checkedIds.has(id));
							// What this account is charged: checked invoice amounts,
							// less the credit applied on this account (PD-15441),
							// plus its on-account deposit (PD-14286).
							const totals = accountTotals.get(acct.clientId) ?? {
								invoiceSum: 0,
								creditApplied: 0,
								deposit: 0,
								net: 0,
								availableCredit: 0,
								creditExceedsInvoices: false,
								creditWithDeposit: false,
								fundsWithDeposit: false,
								fundsApplied: 0,
								fundsCoverAll: false,
								partialFundsBlocked: false,
								zeroTotalBlocked: false,
							};
							const sum = totals.net;
							return (
								<div
									key={acct.clientId}
									className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4"
								>
									{/* Account header + per-account method selector.
									    PD-15585 — STICKY: an account can carry dozens of
									    invoices, and scrolled into the middle of them there
									    was nothing on screen saying whose bill you were
									    looking at. The offset clears the site header (which
									    is itself sticky); the surface background keeps the
									    rows from showing through as they pass underneath. */}
									<div
										className="sticky z-10 bg-brand-surface flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 pt-3 -mt-3 border-b border-brand-border"
										style={{ top: headerOffset }}
									>
										<div className="flex items-center gap-3 min-w-0">
											{acctIds.length > 0 && (
												<Tooltip
													content={t('Select all for this account')}
													position="top"
												>
													<input
														type="checkbox"
														aria-label={t('Select all for this account')}
														checked={acctAllChecked}
														ref={(el) => {
															if (el != null) {
																el.indeterminate =
																	acctSomeChecked && !acctAllChecked;
															}
														}}
														onChange={() => {
															toggleAccountSelectAll(acct.openInvoices);
														}}
														className="w-4 h-4 text-portal-accent shrink-0"
													/>
												</Tooltip>
											)}
											<div className="min-w-0">
												<p className="text-sm font-semibold text-default">
													{t(acct.clientName ?? `#${acct.clientId}`)}
													{/* PD-15585 — the account's own customer number
													    beside its name. A customer can hold several
													    locations trading under one name, which the name
													    alone cannot tell apart; this is the same
													    `name · id` pairing the account menu shows. */}
													{acct.clientExternalId != null && (
														<span className="ml-2 text-xs font-normal text-brand-muted">
															{t('·')} {t(acct.clientExternalId)}
														</span>
													)}
													{/* PD-15098: mark the signed-in account so it can't
													    be confused with the other managed accounts. */}
													{acct.clientId === context?.payerClientId && (
														<span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-portal-accent/10 text-portal-accent align-middle">
															{t('Your account')}
														</span>
													)}
												</p>
												{(sum > 0 || totals.creditApplied > 0) && (
													<p className="text-xs text-brand-muted">
														{t('Paying')}: {t(formatCurrency(sum) ?? '')}
													</p>
												)}
												{/* PD-15441 — say what the credit did to this
												    account's figure, on the account it applies to.
												    The grand total below sums the NETS, so without
												    this the number simply looks too low.
												    PD-15688 — stated as a deduction ("less X credit
												    applied") rather than "includes X", which read as
												    though the credit were inside the figure above. */}
												{totals.creditApplied > 0 && (
													<p className="text-xs text-portal-accent">
														{t('Less {{amount}} credit applied.', {
															amount:
																formatCurrency(totals.creditApplied) ?? '',
														})}
													</p>
												)}
											</div>
										</div>
										{/* PD-15585 — hidden when this account's own balance
										    settles its selection: there is nothing left for a
										    method to collect. */}
										{!totals.fundsCoverAll && (
											<div>
												<label className="block text-xs font-medium text-brand-muted mb-1">
													{t('Pay with')}
												</label>
												{/* PD-16381 — the shared Select rather than a native
												    select, which can only show each method as one
												    crammed line: here, as on every other payment
												    picker, the method is named on the first line and
												    its funding and fee sit smaller on the second. */}
												<div className="w-64 max-w-full">
													<Select
														aria-label={t('Pay with')}
														value={choice}
														onChange={(next) => {
															setAccountChoice((prev) => ({
																...prev,
																[acct.clientId]: next,
															}));
															clearResults();
														}}
														options={[
															{
																value: DEFAULT_CHOICE,
																label: t('Default method'),
															},
															...acct.savedPaymentMethods.map((m) => {
																const option = formatPaymentMethodOptionLabel(
																	m,
																	t,
																	{
																		feeTypes:
																			context?.defaultPaymentTypes ?? [],
																	},
																);
																return {
																	value: String(m.id),
																	label: option.primary,
																	subtitle: option.secondary,
																	selectedSuffix: option.fee,
																	icon: (
																		<CardBrandIcon
																			brand={m.subtype ?? m.type}
																			className="text-xl"
																			title={formatPaymentMethodLabel(m, t)}
																		/>
																	),
																};
															}),
														]}
													/>
												</div>
											</div>
										)}
									</div>

									{/* Invoices for this account */}
									{acct.openInvoices.length === 0 && (
										<p className="text-sm text-brand-muted">
											{filtersActive
												? t('No invoices match your filters.')
												: t('No open invoices on this account.')}
										</p>
									)}
									<div className="space-y-2">
										{acct.openInvoices.map((invoice) => {
											const line = lines[invoice.id] ?? {
												checked: false,
												amount: '',
											};
											const lineError = lineErrors.get(invoice.id);
											return (
												<div
													key={invoice.id}
													className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
												>
													<input
														type="checkbox"
														checked={line.checked}
														onChange={() => {
															toggleLine(
																invoice.id,
																invoice.outstandingBalance,
															);
														}}
														className="w-4 h-4 text-portal-accent"
													/>
													<div className="flex-1 min-w-[120px]">
														<p className="text-sm font-medium text-default">
															{t('Invoice')} #{t(invoice.invoiceNumber)}
														</p>
														<p className="text-xs text-brand-muted">
															{t('Outstanding')}:{' '}
															{t(
																formatCurrency(invoice.outstandingBalance) ??
																	'',
															)}
															{invoice.dueDate != null && (
																<>
																	{' · '}
																	{t('Due')} {t(invoice.dueDate)}
																</>
															)}
														</p>
													</div>
													<div className="w-32">
														<CurrencyInput
															value={line.amount}
															disabled={!line.checked}
															onChange={(next) => {
																setLineAmount(invoice.id, next);
															}}
															className="text-right disabled:opacity-50"
														/>
													</div>
													{lineError != null && (
														<p className="w-full text-xs text-danger text-right">
															{t(lineError)}
														</p>
													)}
												</div>
											);
										})}
									</div>

									{/* PD-15441 — this account's credit memos (RMAs). Applied
									    credit reduces what THIS account is charged and cannot
									    reach another account's invoices: the submit captures one
									    payment per account, and a memo belongs to one client.
									    Rows only arrive when the tenant has
									    payments.pay_invoices_with_credit_memos enabled. */}
									{acct.creditMemos.length > 0 && (
										<div className="mt-3 border-t border-brand-border pt-3">
											<div className="flex items-center justify-between mb-1">
												<h3 className="text-sm font-semibold text-default">
													{t('Credit Memos')}
												</h3>
												<p className="text-xs text-brand-muted">
													{t('Available credit')}:{' '}
													{t(formatCurrency(totals.availableCredit) ?? '')}
												</p>
											</div>
											<p className="text-xs text-brand-muted mb-2">
												{t(
													'Applied credit reduces the amount charged for this account’s invoices above.',
												)}
											</p>
											<div className="space-y-2">
												{acct.creditMemos.map((memo) => {
													const line = lines[memo.id] ?? {
														checked: false,
														amount: '',
													};
													const memoError = lineErrors.get(memo.id);
													return (
														<div
															key={memo.id}
															className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
														>
															<input
																type="checkbox"
																checked={line.checked}
																onChange={() => {
																	toggleCreditMemo(
																		acct,
																		memo.id,
																		memo.outstandingBalance,
																	);
																}}
																className="w-4 h-4 text-portal-accent"
															/>
															<div className="flex-1 min-w-[120px]">
																<p className="text-sm font-medium text-default">
																	{t('Credit Memo')} #{t(memo.invoiceNumber)}
																</p>
																<p className="text-xs text-brand-muted">
																	{t('Remaining credit')}:{' '}
																	{t(
																		formatCurrency(memo.outstandingBalance) ??
																			'',
																	)}
																	{memo.date != null && (
																		<>
																			{' · '}
																			{t('Dated')} {t(memo.date)}
																		</>
																	)}
																</p>
															</div>
															{/* The minus sign is the whole visual difference
															    between paying a document and applying one, so
															    it replaces the dollar prefix rather than
															    sitting beside it. */}
															<div className="w-32">
																<CurrencyInput
																	value={line.amount}
																	disabled={!line.checked}
																	prefix="-$"
																	onChange={(next) => {
																		setLineAmount(memo.id, next);
																	}}
																	className="text-right disabled:opacity-50"
																/>
															</div>
															{memoError != null && (
																<p className="w-full text-xs text-danger text-right">
																	{t(memoError)}
																</p>
															)}
														</div>
													);
												})}
											</div>
											{/* Both guards are also enforced server-side; stating
											    them on the account they belong to is what makes a
											    page of near-identical cards actionable. */}
											{totals.creditExceedsInvoices && (
												<p className="mt-2 text-sm text-danger">
													{t(
														'Applied credit exceeds the selected invoice amounts for this account. Reduce the credit applied or select more invoices.',
													)}
												</p>
											)}
											{totals.creditWithDeposit && (
												<p className="mt-2 text-sm text-danger">
													{t(
														'Credit memos cannot be combined with an Apply to amount on the same account.',
													)}
												</p>
											)}
											{/* PD-15585 — a $0 net this account's credit fully
											    covers, on a tenant whose ERP cannot record it. */}
											{totals.zeroTotalBlocked && (
												<p className="mt-2 text-sm text-danger">
													{t(restrictions.zeroTotalPayment.message)}
												</p>
											)}
										</div>
									)}

									{/* PD-15585 — this account's account-funds opt-in. Account
									    Funds used to be an option inside "Pay with", which let a
									    $2 balance be chosen against a $2,169.93 invoice: the
									    summary then quoted the whole amount as the charge while
									    the server refused the payment for exceeding the balance.
									    As a checkbox the balance can only ever cover what it
									    covers, and the chosen method collects the rest.

									    Sits with the credit memos, below the invoices: both are
									    the customer's own money being put against what they
									    owe, and they are spent in this order — credits first,
									    then funds, then the method.  */}
									{acct.accountFunds.enabled &&
										acct.accountFunds.available > 0 && (
											<div className="mt-3 pt-3 border-t border-brand-border">
												<label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input">
													<input
														type="checkbox"
														checked={useFundsByClient[acct.clientId]}
														onChange={(e) => {
															setUseFundsByClient((prev) => ({
																...prev,
																[acct.clientId]: e.target.checked,
															}));
															clearResults();
														}}
														className="w-4 h-4 text-portal-accent"
													/>
													<FontAwesomeIcon
														icon={faWallet}
														className="text-default"
													/>
													<span className="text-sm text-default">
														{t("Use this account's funds")}
													</span>
													<span className="text-xs text-brand-muted ml-auto">
														{t(
															formatCurrency(acct.accountFunds.available) ?? '',
														)}{' '}
														{t('available')}
													</span>
												</label>
												{/* PD-15585 — this tenant's ERP can only apply an
												    on-account payment in full, so a balance LARGER
												    than the selection cannot be spent at all. */}
												{totals.partialFundsBlocked && (
													<p className="ml-9 text-xs text-danger mt-1">
														{t(
															formatPaymentRestrictionMessage(
																restrictions.partialAccountFundsSpend.message,
																formatCurrency(acct.accountFunds.available) ??
																	'',
															),
														)}
													</p>
												)}
												{totals.fundsWithDeposit && (
													<p className="ml-9 text-xs text-danger mt-1">
														{t(
															'Account funds cannot pay for money added to the same account — clear the Apply to amount.',
														)}
													</p>
												)}
												{useFundsByClient[acct.clientId] &&
													!totals.partialFundsBlocked &&
													!totals.fundsWithDeposit &&
													totals.fundsApplied > 0 && (
														<p className="ml-9 text-xs text-brand-muted mt-1">
															{totals.fundsCoverAll
																? t(
																		'These account funds cover this account — no other payment method is needed.',
																	)
																: `${t('Applies')} ${formatCurrency(totals.fundsApplied) ?? ''} ${t('now; the remaining')} ${formatCurrency(round2(totals.net - totals.fundsApplied)) ?? ''} ${t('is collected from the payment method selected above.')}`}
														</p>
													)}
											</div>
										)}

									{/*
									  On-account deposit for THIS account (PD-14286). Charged to
									  the account's resolved funding method and placed on its
									  balance. Disabled when paying with Account Funds — funds
									  can't be deposited back onto the account. Hidden entirely
									  when the tenant does not allow deposits (PD-14922);
									  spending this account's funds is unaffected.
									*/}
									{canDepositToAccount && (
										<div className="flex flex-wrap items-center gap-3 p-2 -mx-2 mt-3 border-t border-brand-border pt-3">
											<div className="flex-1 min-w-[120px]">
												{/* PD-15098: name the target account in the row itself —
												    scrolled mid-card, "this account" gave no clue whose
												    card the deposit was landing on, and a deposit went
												    to the wrong account that way. */}
												<p className="text-sm font-medium text-default">
													{t('Apply to')}{' '}
													{t(acct.clientName ?? `#${acct.clientId}`)}
												</p>
												<p className="text-xs text-brand-muted">
													{useFundsByClient[acct.clientId]
														? t(
																'Not available while spending this account’s funds.',
															)
														: t(
																'Funds added to this account’s balance instead of an invoice.',
															)}
												</p>
											</div>
											<div className="w-32">
												<CurrencyInput
													value={onAccountByClient[acct.clientId] ?? ''}
													disabled={useFundsByClient[acct.clientId]}
													onChange={(next) => {
														setOnAccountByClient((prev) => ({
															...prev,
															[acct.clientId]: next,
														}));
														clearResults();
													}}
													className="text-right disabled:opacity-50"
												/>
											</div>
										</div>
									)}
								</div>
							);
						})
					)}

					{/* Default payment method (used by accounts set to "Default method") */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-center gap-2 mb-2">
							<FontAwesomeIcon icon={faWallet} className="text-portal-accent" />
							<h2 className="text-sm font-semibold text-default">
								{t('Default payment method')}
							</h2>
						</div>
						<p className="text-xs text-brand-muted mb-3">
							{t(
								'Used for any account set to "Default method" above. Stored on your signed-in account.',
							)}
						</p>
						{(context?.defaultSavedPaymentMethods.length ?? 0) > 0 ? (
							// PD-15878 — the shared Select, so each option carries its network's
							// mark. The fee stays on every option: it is what distinguishes two
							// otherwise identical cards on this screen. PD-16381 — on the option's
							// second line, and beside the name once chosen.
							<Select
								aria-label={t('Default payment method')}
								value={defaultMethodId ?? ''}
								disabled={defaultMethodUnused}
								onChange={(next) => {
									setDefaultMethodId(next === '' ? null : parseInt(next, 10));
									clearResults();
								}}
								options={(context?.defaultSavedPaymentMethods ?? []).map(
									(m) => {
										const option = formatPaymentMethodOptionLabel(m, t, {
											feeTypes: context?.defaultPaymentTypes ?? [],
										});
										return {
											value: m.id,
											label: option.primary,
											subtitle: option.secondary,
											selectedSuffix: option.fee,
											icon: (
												<CardBrandIcon
													brand={m.subtype ?? m.type}
													className="text-xl"
													title={formatPaymentMethodLabel(m, t)}
												/>
											),
										};
									},
								)}
							/>
						) : (
							<p className="text-xs text-brand-muted">
								{t('No saved payment methods on your account yet.')}
							</p>
						)}
						{/* PD-15975 — say WHY the control above is greyed out, on its own
						    line below the field. Without it a disabled dropdown reads as
						    a fault rather than as "nothing here would be used". */}
						{defaultMethodUnused &&
							(context?.defaultSavedPaymentMethods.length ?? 0) > 0 && (
								<p className="text-xs mt-1 text-brand-muted">
									{t(
										'Every selected account already has a payment method chosen.',
									)}
								</p>
							)}
						<button
							type="button"
							onClick={() => {
								setShowAddPayment(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add new payment method')}</span>
						</button>
					</div>

					{/* Total + per-account results */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						{/* PD-15688 — the checkout summary across every selected
						    account. The credit is deducted as its own line instead of
						    being described in a sentence under an already-netted
						    "Total Payment", which read as though it were included in
						    that figure. */}
						<PaymentSummary
							total={round2(total + processingFees - fundsAppliedTotal)}
							lines={[
								{ label: 'Applied to invoices', amount: invoiceSumTotal },
								{
									label: 'Credit memos applied',
									amount: -creditAppliedTotal,
									hideWhenZero: true,
								},
								// PD-15585 — what the account balances take off the
								// charge, deducted as its own line so the cards' share is
								// visible rather than inferred.
								{
									label: 'Account funds applied',
									amount: -fundsAppliedTotal,
									hideWhenZero: true,
								},
								{
									label: 'Applied to my account',
									amount: depositTotal,
									hideWhenZero: true,
								},
								{
									label: 'Processing fees',
									amount: processingFees,
									hideWhenZero: true,
								},
							]}
						/>
						{results != null && (
							<div className="mt-3 border-t border-brand-border pt-3 space-y-1">
								{results.map((r) => (
									<div
										key={r.clientId}
										className="flex items-center justify-between text-sm"
									>
										<span className="text-default">
											{t(accountName(r.clientId))}
										</span>
										{r.success ? (
											<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-success-badge text-success-tint-fg border border-success-tint">
												{t(r.transactionStatus ?? 'Paid')}
											</span>
										) : (
											<span className="text-xs text-danger text-right ml-3">
												{t(r.error ?? 'Failed')}
											</span>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{submitError != null && (
						<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
							<p className="text-sm text-danger">{t(submitError)}</p>
						</div>
					)}

					{/* PD-15585 — why the button below is inert, NAMED PER ACCOUNT.
					    Each reason is also stated on the card it belongs to, but this
					    page can run to dozens of cards: a customer at the bottom had
					    a greyed-out button and no way to tell which account, out of
					    all of them, was the problem. */}
					{submitError == null &&
						!nothingChosenYet &&
						blockingReasons.length > 0 && (
							<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
								<p className="text-sm font-medium text-danger">
									{t('This payment cannot be submitted yet')}
								</p>
								<ul className="mt-1 space-y-0.5">
									{blockingReasons.map((reason) => (
										<li key={reason.key} className="text-sm text-danger">
											{reason.account != null
												? `${t(reason.account)}: ${t(reason.message)}`
												: t(reason.message)}
										</li>
									))}
								</ul>
							</div>
						)}

					{/* PD-15245 — disabled state matches the support page's Send Reply
					    button (accent fill faded via opacity) instead of the CRM
					    `control-strong` token, which read as an unthemed gray block
					    with white text on brand-tinted dark palettes. `enabled:` on
					    the hover fill keeps the disabled button inert under the
					    pointer; the not-allowed cursor still signals why. */}
					<button
						type="button"
						onClick={() => void handleSubmit()}
						disabled={!canSubmit}
						className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-6"
					>
						{submitting ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
						) : null}
						{submitting
							? t('Processing...')
							: total - fundsAppliedTotal <= 0 &&
								  creditAppliedTotal <= 0 &&
								  fundsAppliedTotal > 0
								? // PD-15585 — the balances covered every selected account,
									// so no method is charged. "Pay $0.00" misdescribes it.
									t('Pay with account funds')
								: total - fundsAppliedTotal <= 0 && creditAppliedTotal > 0
									? // PD-15441 — nothing is being charged, so "Pay $0.00"
										// misdescribes the button. The action is recording the
										// credit applications against the invoices.
										t('Apply Credit')
									: // PD-15688 — the fee-inclusive figure the cards are
										// actually charged, matching the summary's Total charge.
										// PD-15585 — less anything the account balances cover.
										t('Pay {{amount}}', {
											amount:
												formatCurrency(
													Math.max(
														0,
														round2(total + processingFees - fundsAppliedTotal),
													),
												) ?? '',
										})}
					</button>
				</>
			)}

			<PortalSidebar
				isOpen={showAddPayment}
				onClose={() => {
					setShowAddPayment(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => {
							setShowAddPayment(false);
							void fetchContext();
						}}
						onCancel={() => {
							setShowAddPayment(false);
						}}
						supportedPaymentMethods={(
							context?.defaultPaymentTypes ?? []
						).filter((pt) => pt.paymentMethodType !== ACCOUNT_FUNDS)}
						preSelectedPaymentType={
							(context?.defaultPaymentTypes ?? []).some(
								(pt) => pt.paymentMethodType === 'ACH',
							)
								? PaymentMethodTypeEnum.ACH
								: PaymentMethodTypeEnum.CREDIT_CARD
						}
					/>
				</div>
			</PortalSidebar>

			{/* PD-15185 — a refused payment, acknowledged deliberately. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</div>
	);
};

export default PayAccountsView;
